import { useEffect, useState } from "react";
import axios from "axios";

export const FALLBACK_NEXT_ACTION_TYPES = [
  "Check Stock",
  "Call Supplier",
  "Call Client",
  "Obtain Proof",
  "Other",
  "Confirm Delivery",
  "Get Tracking",
];

export const FALLBACK_NEXT_ACTION_OPTIONS = FALLBACK_NEXT_ACTION_TYPES.map((name, index) => ({
  id: String(index + 1),
  name,
}));

export const FALLBACK_IMPRINT_TYPES = [
  "1 Color 1 location only",
  "1 Colour Imprint",
  "2D Colour",
  "3D Embroidery",
  "3D Laser Engraving",
  "3D Modelling",
  "3M Reflective Heat Transfer",
  "4CP Patch",
  "4CP",
  "Acrylic Synthetic Heritage Kit",
  "Applique",
  "Aqua Mark",
  "Brand Patch",
  "Brand Shield",
  "Brilliant Print Heat Transfer",
  "BritePix",
  "Chenille",
  "Chocolate Mold",
  "Color Fill",
  "Color Jet",
  "ColorPrint - 2",
  "Crests - Outsourced",
  "Custom Fabric Label",
  "Custom Knit",
  "Custom PVC Shape",
  "Custom Woven Patch",
  "Custom",
  "Cut Vinyl",
  "D4 gloss",
  "Deep Etch",
  "Die Cast",
  "Die Struck",
  "Digi Cal",
  "Digi Print",
  "Digital Inkjet",
  "Digital Print",
  "Digital Transfer",
  "Direct Process",
  "Direct To Garment (DTG)",
  "DTF",
  "Dye Sublimation",
  "Dye",
  "E-2 Printing",
  "Easy Print #'s",
  "Econo Digi Cal",
  "Embossing/Debossing",
  "Embroidered Patch",
  "Embroidery  •  SDT Transfer  •  Pewter  •  Woven L",
  "Embroidery",
  "Enamel",
  "Engraving",
  "Epoxy Dome",
  "Film",
  "Flexographic, 4 Color",
  "Flexstyle Applique",
  "Flocking",
  "Foil Hotstamp",
  "Foil",
  "Foto Color",
  "Full Color Process",
  "Full Colour",
  "Graphic Design/Art Redraw",
  "HD 360",
  "HD Aqua Mark",
  "HD Chroma",
  "HD Decor",
  "HD Ready",
  "HD360 4 color process",
  "Heat transfer",
  "Hot Stamping",
  "HXD",
  "Impression",
  "Infusion",
  "Jacquard Weave",
  "Laser Engraving",
  "Laser Etching",
  "Laser Patch",
  "Laser Plus (PCNA)",
  "LEHR Fired Printing",
  "Lextra",
  "Logo Dome",
  "LogoMagic",
  "Medallion",
  "Mixed Media",
  "Molded Rubber Patch",
  "N/A",
  "No Imprint (Blank Goods)",
  "Offset/Litho Printing",
  "Pad Printing",
  "Perfect Print",
  "Perma Patch",
  "Perma Press",
  "Photo Anodizing",
  "Photo Finish",
  "Photografixx",
  "Photoimage Full Colour",
  "Price includes having a name (up to 2\" in size) pe",
  "PrismMax",
  "Protwill",
  "ReflectInk",
  "Sandblasting",
  "Screen Print",
  "Screened Transfer",
  "Silk Screening",
  "SimpliColor",
  "Sublimated Patch",
  "Sublimated Transfer",
  "Sublimation",
  "Tackle Twill",
  "Texture Tone",
  "Transfer Print",
  "TruColor",
  "True Edge",
  "Two Press Print",
  "UV Print",
  "Vibraprint",
  "VividPrint",
  "Wool Heritage Kit",
  "YOUTH LARGE",
  "YOUTH MEDIUM",
  "YOUTH SMALL",
  "YOUTH XLARGE",
  "YOUTH XSMALL",
];

export const FALLBACK_IMPRINT_OPTIONS = FALLBACK_IMPRINT_TYPES.map((name) => ({
  id: name,
  name,
}));

const instanceIdCache = new Map();
const instanceTermsCache = new Map();
const instanceImprintCache = new Map();

const normalizeNextActionOptions = (types) => {
  if (!Array.isArray(types) || types.length === 0) return null;
  const first = types[0];

  if (typeof first === "string") {
    const names = types.map((name) => String(name).trim()).filter(Boolean);
    if (!names.length) return null;
    return names.map((name) => ({ id: name, name }));
  }

  const options = types
    .map((type) => {
      if (!type) return null;
      const id = type.id ?? type.value ?? type.key ?? type.code;
      const name = type.name ?? type.label ?? type.title ?? type.value ?? "";
      if (id == null || !String(name).trim()) return null;
      return { id: String(id), name: String(name).trim() };
    })
    .filter(Boolean);

  return options.length ? options : null;
};

const buildTermsPayload = (types) => {
  const options = normalizeNextActionOptions(types);
  if (!options) {
    return {
      nextActionTypes: [...FALLBACK_NEXT_ACTION_TYPES],
      nextActionTypeOptions: [...FALLBACK_NEXT_ACTION_OPTIONS],
    };
  }

  return {
    nextActionTypes: options.map((option) => option.name),
    nextActionTypeOptions: options,
  };
};

const normalizeImprintOptions = (types) => {
  if (!Array.isArray(types) || types.length === 0) return null;
  const first = types[0];

  if (typeof first === "string") {
    const names = types.map((name) => String(name).trim()).filter(Boolean);
    if (!names.length) return null;
    return names.map((name) => ({ id: name, name }));
  }

  const options = types
    .map((type) => {
      if (!type) return null;
      const name = type.name ?? type.label ?? type.title ?? type.value ?? "";
      if (!String(name).trim()) return null;
      const cleanName = String(name).trim();
      return { id: cleanName, name: cleanName };
    })
    .filter(Boolean);

  return options.length ? options : null;
};

const buildImprintPayload = (types) => {
  const options = normalizeImprintOptions(types);
  if (!options) {
    return {
      imprintTypes: [...FALLBACK_IMPRINT_TYPES],
      imprintTypeOptions: [...FALLBACK_IMPRINT_OPTIONS],
    };
  }

  return {
    imprintTypes: options.map((option) => option.name),
    imprintTypeOptions: options,
  };
};

const fetchInstanceId = async (instanceUniqueId) => {
  if (!instanceUniqueId) return null;
  if (instanceIdCache.has(instanceUniqueId)) return instanceIdCache.get(instanceUniqueId);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };
  const res = await axios.get(
    `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
    headers
  );
  const instanceId = res?.data?.id ?? null;
  if (instanceId) instanceIdCache.set(instanceUniqueId, instanceId);
  return instanceId;
};

export const getInstanceTerms = async (instanceUniqueId) => {
  if (!instanceUniqueId) {
    return {
      nextActionTypes: [...FALLBACK_NEXT_ACTION_TYPES],
      nextActionTypeOptions: [...FALLBACK_NEXT_ACTION_OPTIONS],
    };
  }

  const cached = instanceTermsCache.get(instanceUniqueId);
  if (cached?.data) return cached.data;
  if (cached?.promise) return cached.promise;

  const promise = (async () => {
    try {
      const instanceId = await fetchInstanceId(instanceUniqueId);
      if (!instanceId) {
        return buildTermsPayload([]);
      }
      const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
      const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };
      const res = await axios.get(
        `${SERVER}/instances/${instanceId}/next-action-types`,
        headers
      );
      const types = res?.data?.types ?? res?.data?.nextActionTypes ?? [];
      return buildTermsPayload(types);
    } catch (error) {
      return buildTermsPayload([]);
    }
  })();

  instanceTermsCache.set(instanceUniqueId, { promise });
  const data = await promise;
  instanceTermsCache.set(instanceUniqueId, { data });
  return data;
};

export const getNextActionTypeOptions = async (instanceUniqueId) => {
  const terms = await getInstanceTerms(instanceUniqueId);
  return terms.nextActionTypeOptions;
};

export const getImprintTypeOptions = async (instanceUniqueId) => {
  if (!instanceUniqueId) {
    return [...FALLBACK_IMPRINT_OPTIONS];
  }

  const cached = instanceImprintCache.get(instanceUniqueId);
  if (cached?.data) return cached.data.imprintTypeOptions;
  if (cached?.promise) {
    const data = await cached.promise;
    return data.imprintTypeOptions;
  }

  const promise = (async () => {
    try {
      const instanceId = await fetchInstanceId(instanceUniqueId);
      if (!instanceId) {
        return buildImprintPayload([]);
      }
      const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
      const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };
      const res = await axios.get(
        `${SERVER}/instances/${instanceId}/customization-methods`,
        headers
      );
      const types = res?.data?.methods ?? res?.data?.imprintTypes ?? [];
      return buildImprintPayload(types);
    } catch (error) {
      return buildImprintPayload([]);
    }
  })();

  instanceImprintCache.set(instanceUniqueId, { promise });
  const data = await promise;
  instanceImprintCache.set(instanceUniqueId, { data });
  return data.imprintTypeOptions;
};

export const useInstanceNextActionTypes = (instanceUniqueId) => {
  const [state, setState] = useState(() => ({
    nextActionTypes: [...FALLBACK_NEXT_ACTION_TYPES],
    nextActionTypeOptions: [...FALLBACK_NEXT_ACTION_OPTIONS],
    loading: true,
  }));

  useEffect(() => {
    let active = true;
    setState((prev) => ({
      ...prev,
      loading: true,
    }));

    getInstanceTerms(instanceUniqueId)
      .then((data) => {
        if (!active) return;
        setState({
          nextActionTypes: data.nextActionTypes,
          nextActionTypeOptions: data.nextActionTypeOptions,
          loading: false,
        });
      })
      .catch(() => {
        if (!active) return;
        setState((prev) => ({ ...prev, loading: false }));
      });

    return () => {
      active = false;
    };
  }, [instanceUniqueId]);

  return state;
};

export const useInstanceImprintTypes = (instanceUniqueId) => {
  const [state, setState] = useState(() => ({
    imprintTypes: [...FALLBACK_IMPRINT_TYPES],
    imprintTypeOptions: [...FALLBACK_IMPRINT_OPTIONS],
    loading: true,
  }));

  useEffect(() => {
    let active = true;
    setState((prev) => ({
      ...prev,
      loading: true,
    }));

    getImprintTypeOptions(instanceUniqueId)
      .then((options) => {
        if (!active) return;
        setState({
          imprintTypes: options.map((option) => option.name),
          imprintTypeOptions: options,
          loading: false,
        });
      })
      .catch(() => {
        if (!active) return;
        setState((prev) => ({ ...prev, loading: false }));
      });

    return () => {
      active = false;
    };
  }, [instanceUniqueId]);

  return state;
};
